import { Alert } from '@mui/material';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { RelatedTicketsTable } from '~/components/invoices/detailPage/related/RelatedTicketsTable';
import { useTickets } from '~/data/hooks/useTickets';
import type { EntryTypeKey } from '~/data/schemas/ticket';
import { ticketOrderingParam } from '~/hooks/useTicketsForTable';

const DEFAULT_PAGE_SIZE = 25;

/**
 * Every ticket ever raised on a set of elevators. Keyed on car ids rather than on the
 * surface asking, so the invoice page passes the cars an invoice serviced and an
 * elevator's own page passes itself.
 *
 * Server-paginated via the `cars` filter: a long-tenured elevator can have hundreds of
 * tickets, and filtering a property-wide page in the browser would miss anything past
 * the first one. Sorting and filtering follow the paging to the server for the same
 * reason — run in the browser they would only reach the page in hand.
 */
export function ElevatorTicketsTable({
  carIds,
  emptyMessage,
  previewList,
}: {
  carIds: string[];
  previewList: 'invoice_car_tickets' | 'elevator_tickets';
  /** Shown instead of the table when there are no cars to scope the read to. */
  emptyMessage: ReactNode;
}) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'startedTime', sort: 'desc' },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // A reordered or narrower list makes the page in hand the wrong one to be on.
  const toFirstPage = useCallback(() => setPaginationModel((prev) => ({ ...prev, page: 0 })), []);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      toFirstPage();
    },
    [toFirstPage],
  );

  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      toFirstPage();
    },
    [toFirstPage],
  );

  // Only the columns this table leaves filterable, since anything else the grid could
  // hold would have no endpoint parameter to go to.
  const filterParams = useMemo(() => {
    const number = filterModel.items.find((item) => item.field === 'number')?.value;
    const entryType = filterModel.items.find((item) => item.field === 'entryType')?.value as
      | EntryTypeKey[]
      | undefined;
    const search = filterModel.quickFilterValues?.join(' ');

    return {
      number: typeof number === 'string' && number !== '' ? number : undefined,
      entryType: entryType?.length ? entryType : undefined,
      search: search || undefined,
    };
  }, [filterModel]);

  const { tickets, numTickets, isLoading } = useTickets(
    {
      cars: carIds,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      ordering: ticketOrderingParam(sortModel) ?? '-startedTime',
      ...filterParams,
    },
    // Without the guard `cars: []` is not a filter, it is no filter: the endpoint
    // answers with every ticket in the tenant, and the component discards the lot and
    // renders the empty state below. Same guard `ElevatorInvoicesTable` passes.
    carIds.length > 0,
  );

  if (carIds.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <RelatedTicketsTable
      previewList={previewList}
      tickets={tickets}
      isLoading={isLoading}
      tableHeaderTitle="Tickets"
      rowCount={numTickets}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
    />
  );
}
